/**
 * Master Dashboard — denser two-column overview.
 * Left column: KPIs + Tenants by package
 * Right column: Apify balance + Recent activity + Quick actions
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  Users, Package, Activity, Crown, Sparkles, BarChart3,
  ArrowRight, ScrollText, Zap, Bot,
} from "lucide-react";
import MasterLayout from "./MasterLayout";
import ScrapingHealthCard from "../../components/ScrapingHealthCard";
import HealthScoreCard from "../../components/HealthScoreCard";
import GovGeocodeMigrationCard from "../../components/GovGeocodeMigrationCard";
import SmithCronHealthCard from "../../components/SmithCronHealthCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_LABELS = {
  blog_cron_published: "Blog post publicado",
  blog_cron_failed: "Blog cron falhou",
  tenant_created: "Novo tenant criado",
  impersonation_start: "Master impersonou tenant",
  ab_variant_auto_paused: "Variante A/B auto-pausada",
  youtube_scrape_sync: "YouTube scrape concluído",
  linkedin_scrape_sync: "LinkedIn scrape concluído",
  apify_low_balance: "Saldo Apify crítico",
};

function formatRelative(iso) {
  if (!iso) return "—";
  try {
    const diff = Math.max(0, (Date.now() - new Date(iso).getTime()) / 1000);
    if (diff < 60) return `${Math.floor(diff)}s atrás`;
    if (diff < 3600) return `${Math.floor(diff / 60)}min atrás`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h atrás`;
    return `${Math.floor(diff / 86400)}d atrás`;
  } catch { return "—"; }
}

export default function MasterDashboard() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);

  useEffect(() => {
    axios.get(`${API}/master/stats`).then((r) => setStats(r.data)).catch(() => setStats(null));
    axios.get(`${API}/master/access-logs?limit=8`)
      .then((r) => setActivity(r.data?.logs || r.data?.items || r.data || []))
      .catch(() => setActivity([]));
  }, []);

  return (
    <MasterLayout title="Master Dashboard">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-2xl font-extrabold tracking-tight">Overview</h2>
        <div className="text-[11px] text-white/45 uppercase tracking-wider">
          {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "2-digit", month: "short", year: "numeric" })}
        </div>
      </div>

      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <KpiTile to="/master/tenants"                   icon={Users}    label="Total clients" value={stats?.tenants?.total ?? "—"} accent="#C9A24E" testid="master-kpi-tenants" />
        <KpiTile to="/master/tenants?status=active"     icon={Crown}    label="Active"        value={stats?.tenants?.active ?? "—"} accent="#10B981" testid="master-kpi-active" />
        <KpiTile to="/master/analytics"                 icon={Package}  label="Total leads"   value={stats?.leads ?? "—"}            accent="#3B82F6" testid="master-kpi-leads" />
        <KpiTile to="/master/tenants"                   icon={Activity} label="Total users"   value={stats?.users ?? "—"}            accent="#A78BFA" testid="master-kpi-users" />
      </div>

      {/* Two-column body */}
      <div className="grid grid-cols-1 xl:grid-cols-[1.35fr_1fr] gap-4">
        {/* LEFT COLUMN */}
        <div className="space-y-4">
          {/* Tenants by package */}
          <div className="rounded-xl p-5 border"
            style={{ borderColor: "rgba(201,162,78,0.2)", background: "rgba(255,255,255,0.03)" }}>
            <div className="flex items-center justify-between mb-4">
              <div className="text-[11px] uppercase tracking-wider text-[#C9A24E] font-bold">
                Tenants by package
              </div>
              <Link to="/master/tenants" data-testid="master-dash-view-tenants"
                className="text-[11px] text-white/55 hover:text-white inline-flex items-center gap-1">
                Ver todos <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2.5">
              {(stats?.by_package || []).map((p) => (
                <Link
                  key={p.id}
                  to={`/master/tenants?pkg=${encodeURIComponent(p.slug || p.id)}`}
                  data-testid={`master-pkg-tile-${p.slug}`}
                  className="rounded-lg p-3 border flex items-center justify-between transition-all hover:-translate-y-0.5 hover:shadow-lg"
                  style={{ borderColor: `${p.color}55`, background: `${p.color}12` }}
                >
                  <div>
                    <div className="text-[10px] font-bold uppercase tracking-wider truncate" style={{ color: p.color }}>{p.name}</div>
                    <div className="text-[22px] font-extrabold text-white mt-0.5 leading-none">{p.count}</div>
                  </div>
                  <Package className="w-5 h-5 shrink-0" style={{ color: p.color, opacity: 0.6 }} />
                </Link>
              ))}
              {(!stats?.by_package || stats.by_package.length === 0) && (
                <div className="col-span-full text-white/45 text-[12.5px] italic">Sem pacotes cadastrados.</div>
              )}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="rounded-xl p-5 border"
            style={{ borderColor: "rgba(201,162,78,0.2)", background: "rgba(255,255,255,0.03)" }}>
            <div className="text-[11px] uppercase tracking-wider text-[#C9A24E] font-bold mb-3">
              Ações rápidas
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-2">
              <QuickAction to="/master/content"   icon={Sparkles}  label="Gerar artigo IA"   color="#A78BFA" testid="master-qa-content" />
              <QuickAction to="/master/analytics" icon={BarChart3} label="A/B Analytics"     color="#10B981" testid="master-qa-analytics" />
              <QuickAction to="/master/tenants"   icon={Users}     label="Gerenciar clientes" color="#C9A24E" testid="master-qa-tenants" />
              <QuickAction to="/master/agents"    icon={Bot}       label="Agentes"           color="#F59E0B" testid="master-qa-agents" />
              <QuickAction to="/master/packages"  icon={Package}   label="Pacotes"           color="#3B82F6" testid="master-qa-packages" />
              <QuickAction to="/master/access-logs" icon={ScrollText} label="Logs completos"  color="#64748B" testid="master-qa-logs" />
            </div>
          </div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="space-y-4">
          <HealthScoreCard />
          <ScrapingHealthCard />
          <GovGeocodeMigrationCard />
          <SmithCronHealthCard />

          {/* Recent Activity */}
          <div className="rounded-xl p-5 border"
            style={{ borderColor: "rgba(201,162,78,0.2)", background: "rgba(255,255,255,0.03)" }}>
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Zap className="w-4 h-4 text-[#C9A24E]" />
                <span className="text-[11px] uppercase tracking-wider text-[#C9A24E] font-bold">Atividade recente</span>
              </div>
              <Link to="/master/access-logs" data-testid="master-dash-view-logs"
                className="text-[11px] text-white/55 hover:text-white inline-flex items-center gap-1">
                Ver tudo <ArrowRight className="w-3 h-3" />
              </Link>
            </div>
            {activity.length === 0 && (
              <div className="text-white/45 text-[12.5px] italic" data-testid="master-activity-empty">
                Sem atividade registrada nas últimas horas.
              </div>
            )}
            <ul className="space-y-2" data-testid="master-activity-list">
              {activity.slice(0, 8).map((ev, i) => (
                <li key={ev.id || ev.ts || i}
                  className="flex items-start gap-2 text-[12.5px] border-b last:border-b-0 pb-2 last:pb-0"
                  style={{ borderColor: "rgba(201,162,78,0.08)" }}>
                  <span className="w-1.5 h-1.5 rounded-full mt-1.5 shrink-0" style={{ background: "#C9A24E" }} />
                  <div className="flex-1 min-w-0">
                    <div className="text-white/85 truncate">
                      {ACTION_LABELS[ev.action] || ev.action || "—"}
                    </div>
                    <div className="text-[10.5px] text-white/40 mt-0.5 flex items-center gap-2">
                      <span>{formatRelative(ev.ts)}</span>
                      {ev.tenant_id && <span className="font-mono truncate">{String(ev.tenant_id).slice(0, 8)}…</span>}
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </MasterLayout>
  );
}

function KpiTile({ icon: Icon, label, value, accent, testid, to }) {
  return (
    <Link
      to={to || "#"}
      data-testid={testid}
      className="rounded-xl p-4 border flex items-center gap-3 transition-all hover:-translate-y-0.5 hover:shadow-lg"
      style={{ borderColor: `${accent}33`, background: `${accent}0C` }}>
      <div className="rounded-lg p-2 shrink-0" style={{ background: `${accent}22` }}>
        <Icon className="w-5 h-5" style={{ color: accent }} />
      </div>
      <div className="min-w-0">
        <div className="text-[22px] font-extrabold text-white leading-none">{value}</div>
        <div className="text-[10px] uppercase tracking-wider text-white/55 font-bold mt-1 truncate">{label}</div>
      </div>
    </Link>
  );
}

function QuickAction({ to, icon: Icon, label, color, testid }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-[12.5px] font-semibold text-white/85 hover:text-white transition-all hover:-translate-y-0.5"
      style={{ borderColor: `${color}40`, background: `${color}12` }}
    >
      <Icon className="w-4 h-4 shrink-0" style={{ color }} />
      <span className="truncate">{label}</span>
    </Link>
  );
}
